/**
 * Lightbox - A reusable lightbox module with zoom, pan, keyboard navigation, and touch support.
 *
 * Usage:
 *
 * 1. Auto-init via data attributes:
 *    <img src="..." data-lightbox>
 *    <img src="..." data-lightbox data-lightbox-group="gallery1">
 *    <img src="..." data-lightbox data-lightbox-caption="Custom caption">
 *    <img src="..." data-lightbox data-lightbox-src="full-size.jpg">
 *
 * 2. JavaScript API:
 *    Lightbox.open([{ src: 'image.jpg', alt: 'Caption' }], 0);
 *    Lightbox.close();
 *    Lightbox.init(); // Re-init after dynamic content
 *    Lightbox.init(document.querySelector('.container')); // Init specific container
 */

import type { LightboxState, LightboxElements, LightboxImage } from './types';
import { createState } from './state';
import { findLightboxElement, getElements } from './dom';
import { getLightboxSrcData, loadImageWithFallback } from './imageLoader';
import { resetZoom } from './zoom';
import { showImage } from './navigation';
import { bindElementEvents, initDocumentListeners, handleElementClick } from './events';
import { collectGroupImages } from './utils';

const RESIZE_DEBOUNCE_MS = 250;

let lightboxElement: HTMLElement | null = null;
let elements: LightboxElements | null = null;
let state: LightboxState = createState();
let documentListenersInitialized = false;
let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
let lastWindowWidth = 0;

function handleResize(): void {
    if (window.innerWidth === lastWindowWidth) {
        return;
    }

    lastWindowWidth = window.innerWidth;

    if (resizeTimeout) {
        clearTimeout(resizeTimeout);
    }

    resizeTimeout = setTimeout(() => {
        if (!state.isOpen || !state.currentImages[state.currentIndex]) {
            return;
        }

        const image = state.currentImages[state.currentIndex];

        if (!image.element) {
            return;
        }

        const newSrcData = getLightboxSrcData(image.element);

        if (!newSrcData || !elements) {
            return;
        }

        image.srcData = newSrcData;

        loadImageWithFallback(newSrcData).then((src) => {
            if (state.currentIndex === state.currentImages.indexOf(image) && elements) {
                elements.img.src = src;
            }
        });
    }, RESIZE_DEBOUNCE_MS);
}

function initLightboxElement(): boolean {
    if (lightboxElement && elements) {
        return true;
    }

    lightboxElement = findLightboxElement();

    if (!lightboxElement) {
        return false;
    }

    elements = getElements(lightboxElement);
    bindElementEvents(state, elements, { close });

    return true;
}

function open(images: LightboxImage[], startIndex: number = 0): void {
    if (!images || images.length === 0) {
        return;
    }

    if (!initLightboxElement() || !elements || !lightboxElement) {
        return;
    }

    if (!documentListenersInitialized) {
        initDocumentListeners(state, elements, { close });
        documentListenersInitialized = true;
    }

    state.currentImages = images;
    state.currentIndex = startIndex;

    showImage(state, elements, state.currentIndex);
    lightboxElement.classList.add('is-open');
    lightboxElement.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    state.isOpen = true;
    lastWindowWidth = window.innerWidth;
    window.addEventListener('resize', handleResize);

    elements.closeBtn.focus();
}

function close(): void {
    if (!lightboxElement || !elements) {
        return;
    }

    lightboxElement.classList.remove('is-open');
    lightboxElement.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    state.isOpen = false;
    resetZoom(state, elements);
    window.removeEventListener('resize', handleResize);

    if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
    }
}

function init(container: HTMLElement | Document = document): void {
    container.querySelectorAll<HTMLElement>('[data-lightbox]').forEach((el) => {
        if (el.hasAttribute('data-lightbox-initialized')) {
            return;
        }

        el.setAttribute('data-lightbox-initialized', 'true');
        el.style.cursor = 'pointer';
        el.addEventListener('click', (e) => {
            handleElementClick(e, collectGroupImages, open);
        });
    });
}

export default { init, open, close };
